import { Grid, useMediaQuery, useTheme } from "@material-ui/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";

import { EmptyDetailsState } from "../../../components";
import { Button } from "../../../components/Button";
import { CollectibleType } from "../../../models/collectible";
import { getParticipantCollectibles } from "../../services";
import { Collectible } from "../collectible/collectible.component";
import { CollectiblesFilters } from "../collectibles-filters/collectibles-filters.component";
import { sectionSharedCollectibles } from "./collectibles.consts";
import { CollectiblesStyles } from "./collectibles.styles";

const PAGE_SIZE = 24;
const INIT_PAGE = 1;

interface Chip {
  id: number;
  title: string;
}

export const Collectibles = () => {
  const classes = CollectiblesStyles();
  const theme = useTheme();

  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(INIT_PAGE);

  const [typeChips, setTypeChips] = useState<Chip[]>([]);

  const [debouncedKeyWord, setDebouncedKeyWord] = useState<
    string | undefined
  >();

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["collectibles", typeChips, debouncedKeyWord],
      queryFn: ({ pageParam }) =>
        getParticipantCollectibles({
          pageNumber: pageParam === true ? INIT_PAGE : pageParam,
          pageSize: PAGE_SIZE,
          collectableType:
            typeChips.length > 0
              ? typeChips.map((chip) => chip.title).join(" ")
              : null,
          keyword: debouncedKeyWord,
        }),
      retry: 0,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => {
        const totalPage = Math.floor(lastPage.totalRecords / PAGE_SIZE);

        return page <= totalPage;
      },
    });

  const onNextPage = () => {
    setPage((prevState) => {
      const statePage = prevState + 1;
      fetchNextPage({ pageParam: statePage });
      return statePage;
    });
  };

  return (
    <Grid
      container
      data-testid="collectibles-container"
      className={classes.root}
      justifyContent={smDown ? "center" : "flex-start"}
    >
      {!data?.pages[0]?.totalRecords &&
      !isFetching &&
      !typeChips.length &&
      !debouncedKeyWord ? (
        <EmptyDetailsState
          title={sectionSharedCollectibles.title}
          imageUrl={sectionSharedCollectibles.imageUrl}
          description={sectionSharedCollectibles.description}
          action={<></>}
        />
      ) : (
        <>
          <CollectiblesFilters
            typeChips={typeChips}
            setTypeChips={setTypeChips}
            debouncedKeyWord={debouncedKeyWord}
            setDebouncedKeyWord={setDebouncedKeyWord}
          />
          {data?.pages.map((page, i) => {
            return (
              <Fragment key={i}>
                {page?.data.map((item) => {
                  return (
                    <Grid
                      item
                      key={item.collectableId}
                      justifyContent="space-between"
                      className={classes.container}
                    >
                      <Collectible
                        key={item.collectableId}
                        collectableType={
                          item.collectableType as CollectibleType
                        }
                        title={item.title}
                        assignedByFullName={item.assignedByFullName}
                        contentLink={item.contentLink}
                      />
                    </Grid>
                  );
                })}
              </Fragment>
            );
          })}
          <Grid
            container
            justifyContent="center"
            alignContent="center"
            className={classes.button}
          >
            {hasNextPage && (
              <Button
                data-testid="button-load-more"
                onClick={() => {
                  if (!isFetchingNextPage) {
                    onNextPage();
                  }
                }}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading more..." : "Load more"}
              </Button>
            )}
            <div>{isFetching && !isFetchingNextPage ? "Loading" : null}</div>
          </Grid>
        </>
      )}
    </Grid>
  );
};
